import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import colors from '@prezzee/ribbon-ui/lib/theme/foundations/colors';
import CaretDownIcon from '@prezzee/ribbon-ui/lib/components/icons/CaretDownIcon';
import { eq, find, isUndefined, size } from 'lodash';

import { DrainContext, FilterCurrent } from 'contexts/DrainContext';
import { FilterTaxonomyOptions } from 'api/queries/filter';

import SelectFilterPopup from './SelectFilterPopup';

import { Color } from 'css/theme';

const Styles = {
  container: css`
    display: inline-block;
    position: relative;
  `,

  caret: isOpen => css`
    position: absolute;
    right: 20px;
    top: 50%;
    transform: ${!isOpen ? 'translateY(-50%)' : 'translateY(-50%) rotate(-180deg)'};
    transform-origin: center;
    transition: transform 200ms ease;

    &:hover {
      color: ${isOpen ? Color.White : colors.gray[800]};
    }
  `,

  button: (isOpen, isActiveFilter) => css`
    width: 100%;
    position: relative;
    display: inline-block;
    border-radius: 100px;
    padding: 12px 40px 12px 20px;
    border: 1px solid ${colors.purple[400]};
    cursor: pointer;
    background: #ffffff;
    white-space: nowrap;
    text-align: left;
    color: ${isOpen ? Color.White : Color.Gray800};

    &:hover {
      background-color: ${colors.purple[400]};
      color: ${Color.White};
    }

    &::after {
      content: '';
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: ${colors.notification.blue};
      position: absolute;
      top: 1px;
      right: 1px;
      opacity: ${isActiveFilter ? '1' : '0'};
      transition: opacity 150ms ease;
    }

    ${isOpen
      ? `background-color: ${colors.purple[400]};
      color: #FFFFFF`
      : ''}
  `,
};

export type ItemType = {
  codename: string;
  name: string;
  label?: string;
  iconSrc?: string;
};

type Props = {
  filterOptions: FilterTaxonomyOptions;
  title: string;
  items: ItemType[];
  iconsGrid?: boolean;
  popupRight?: boolean;
  searchLabel?: string;
  className?: string;
  applyCallback: (filter: FilterCurrent, activeItems: ItemType[]) => void;
  parentCodename: string;
  isSearchBar: boolean;
};

const SelectFilter: FC<Props> = ({
  filterOptions,
  className,
  title,
  parentCodename,
  items,
  searchLabel = '',
  iconsGrid = false,
  popupRight = false,
  applyCallback,
  isSearchBar,
}) => {
  const drain = useContext(DrainContext);

  const isActive = !isUndefined(
    find(drain.currentValue.filter.elements, ({ parent: { codename } }) => eq(codename, parentCodename))
  );

  const [isOpen, setOpen] = useState<boolean>(false);
  const [isActiveFilter, setActiveFilter] = useState<boolean>(isActive);

  const wrapperRef = useRef(null);

  const closePopup = () => {
    setOpen(false);
  };

  const closeOnOutsideClick = useCallback((e: MouseEvent) => {
    if (wrapperRef.current && wrapperRef.current !== e.target && !wrapperRef.current.contains(e.target)) {
      closePopup();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', closeOnOutsideClick);
    } else {
      document.removeEventListener('click', closeOnOutsideClick);
    }

    return () => {
      document.removeEventListener('click', closeOnOutsideClick);
    };
  }, [isOpen]);

  const toggleOpen = () => {
    setOpen(!isOpen);
  };

  const onApply = useCallback(
    (filter: FilterCurrent, activeItems: ItemType[]) => {
      setActiveFilter(size(activeItems) > 0);
      applyCallback(filter, activeItems);
    },
    [drain.filter, drain.sort, drain.search]
  );

  return (
    <div className={`select-filter ${Styles.container} ${className}`} ref={wrapperRef}>
      <button className={Styles.button(isOpen, isActiveFilter)} onClick={toggleOpen}>
        {title}
        <CaretDownIcon className={Styles.caret(isOpen)} />
      </button>
      <SelectFilterPopup
        filterOptions={filterOptions}
        title={title}
        iconsGrid={iconsGrid}
        isOpen={isOpen}
        closePopup={closePopup}
        items={items}
        onApply={onApply}
        popupRight={popupRight}
        searchLabel={searchLabel}
        parentCodename={parentCodename}
        isSearchBar={isSearchBar}
      />
    </div>
  );
};

export default SelectFilter;
